/* eslint-disable no-console */
import _ from 'lodash';
import { batch } from 'react-redux';

import { setTokenStatus } from './AuthActions';
import { setEnhancedModalOpen } from './EnhancedModalActions';
import { clearErrors, handleErrors } from './ErrorActions';
import { deleteEntityFromLocalDB, downloadSavedSearchResults } from './OfflineDataActions';
import { addSuccess, clearSuccesses } from './SuccessActions';
import { addWarning } from './WarningActions';
import { sendBatchUpdate } from '../api/batch-update';
import { sendBulkUpload } from '../api/bulkupload';
import { getRequestControlPrint, getRequestControlPrintBatch } from '../api/document';
import smartSolveApi from '../api/instances/smartsolve';
import { WARNING_SOURCES } from '../constants/WarningsConstants';
import { EntitiesDBService } from '../db/entitiesDBService';
import {
  setUserMadeInteraction,
  updateOriginalData,
} from '../form-designer/actions/runtime.actions';
import { FORM_KEYS } from '../form-designer/constants/form-manager.constants';
import { getSmartSolveDiff } from '../form-designer/utils/diffTracking.utils';
import { modifyBatchPrintClientChanges } from '../utils/commonUtils';
import { endTimer, SUBMIT_TIMER } from '../utils/log';
import printJS from '../utils/pdf-print-js';
import { saveStorageItem } from '../utils/storageUtils';

export const PERFORM_WORKFLOW_STEP_SUCCESS = 'PERFORM_WORKFLOW_STEP_SUCCESS';
export const ON_SUBMIT_START = 'ON_SUBMIT_START';
export const ON_SUBMIT_END = 'ON_SUBMIT_END';
export const SET_SUBMIT_WORKFLOW_RESULT = 'SET_SUBMIT_WORKFLOW_RESULT';
export const START_SUBMIT_WORKFLOW_API_REQUEST = 'START_SUBMIT_WORKFLOW_API_REQUEST';
export const ABORT_SUBMIT_WORKFLOW_API_REQUEST = 'ABORT_SUBMIT_WORKFLOW_API_REQUEST';

export const START_AUTO_CHECKING = 'START_AUTO_CHECKING';
export const STOP_AUTO_CHECKING = 'STOP_AUTO_CHECKING';

export const SET_FINISH_SUBMIT = 'SET_FINISH_SUBMIT';
export const GET_SIGNOFF_TRANSLATION_SUCCESS = 'GET_SIGNOFF_TRANSLATION_SUCCESS';

export const SET_IS_WORKFLOW_REQUEST_PENDING = 'SET_IS_WORKFLOW_REQUEST_PENDING';
export const CLEAR_WORKFLOW = 'CLEAR_WORKFLOW';
export const SET_DECISION_TREE_IFRAME_SRC = 'SET_DECISION_TREE_IFRAME_SRC';
export const SET_INSPECTION_MEASUREMENT_DATA_IFRAME_SRC =
  'SET_INSPECTION_MEASUREMENT_DATA_IFRAME_SRC';
export const SET_MIDDLE_PANEL_LOADING = 'SET_MIDDLE_PANEL_LOADING';

export function setFinishSubmit(value) {
  return {
    type: SET_FINISH_SUBMIT,
    payload: {
      value,
    },
  };
}

export function startAutoChecking() {
  return {
    type: START_AUTO_CHECKING,
  };
}

export function stopAutoChecking() {
  return {
    type: STOP_AUTO_CHECKING,
  };
}

export function setSubmitWorkflowResult(submitWorkflowResult) {
  const time = endTimer(SUBMIT_TIMER);
  // eslint-disable-next-line no-console
  console.log(`🕑 Submit workflow time: ${time}s`);
  return {
    type: SET_SUBMIT_WORKFLOW_RESULT,
    payload: {
      submitWorkflowResult,
    },
  };
}

export const setDecisionTreeIframeSrc = (decisionTreeIframeSrc) => ({
  type: SET_DECISION_TREE_IFRAME_SRC,
  decisionTreeIframeSrc,
});

export const setInspectionMeasurementDataIframeSrc = (inspectionMeasurementDataIframeSrc) => ({
  type: SET_INSPECTION_MEASUREMENT_DATA_IFRAME_SRC,
  inspectionMeasurementDataIframeSrc,
});

export function loadWorkflowStepSuccess(workFlowSteps) {
  return {
    type: PERFORM_WORKFLOW_STEP_SUCCESS,
    workFlowSteps,
  };
}

export function onSubmitEnd() {
  return (dispatch) => {
    dispatch({ type: ON_SUBMIT_END });
  };
}

export const setIsWorkflowRequestPending = (flag) => ({
  type: SET_IS_WORKFLOW_REQUEST_PENDING,
  payload: { flag },
});

export const clearWorkflow = () => ({
  type: CLEAR_WORKFLOW,
});

export function saveEntityDataToDB(
  clientChanges,
  containerWorkflowKey,
  workflowKey,
  currentWorkflowId,
  itmId,
  stepId,
  commandName = '',
  commandArgument = '',
  withSignOff = false
) {
  return async (dispatch, getState) => {
    const {
      workflow: { targetForm },
      formDesigner,
      taskStatusPanel: { start, tasks, disabled },
      translations: { Form, Entity, entityResolverRules },
      offlineData: { entities },
    } = getState();

    const {
      data,
      originalData,
      formModel,
      entityDefinition,
      formInfo,
      workflowInfo,
      formRules,
      dataSources,
    } = formDesigner[targetForm];

    const detailForm = formDesigner[FORM_KEYS.DETAIL];

    const stepObj = {
      formModel,
      entityDefinition,
      dataSources,
      formInfo,
      workflowInfo,
      formRules,
      originalData,
      data,
      taskStatus: { start, tasks, disabled },
      translations: { Form, Entity },
      entityResolverRules,
    };

    const detailObj = {
      formModel: detailForm.formModel,
      dataSources: detailForm.dataSources,
      formInfo: detailForm.formInfo,
      workflowInfo: detailForm.workflowInfo,
      formRules: detailForm.formRules,
      data: detailForm.data,
    };

    const itemId = `${itmId}_${stepId}`;
    const offlineFiles = _.get(_.find(entities, { id: itemId }), 'files', []);

    const changes = {
      clientChanges,
      currentWorkflowId,
      containerWorkflowKey,
      workflowKey,
      itmId,
      stepId,
      withSignOff,
      commandName,
      commandArgument,
      password: '',
      parentWorkflowId: '',
    };

    const entityDBId = `${itemId} + ${localStorage.getItem('userCacheKey')}`;
    const entityData = {
      requestChanges: changes,
      entity: {
        step: stepObj,
        detail: detailObj,
      },
      files: offlineFiles,
      id: entityDBId,
    };

    batch(() => {
      dispatch(setIsWorkflowRequestPending(true));
      // cleanup previous successes/errors/warning
      dispatch(clearSuccesses());
      dispatch(clearErrors());
      dispatch(setUserMadeInteraction(targetForm, false));
    });

    return EntitiesDBService.saveEntityDataAndChanges(entityData)
      .then(() => {
        dispatch(downloadSavedSearchResults({ itmId, stepId, data }));
        dispatch(setIsWorkflowRequestPending(false));
        dispatch(onSubmitEnd());
        dispatch({ type: ABORT_SUBMIT_WORKFLOW_API_REQUEST });
      })
      .catch((error) => {
        dispatch(setIsWorkflowRequestPending(false));
        console.log(`saveEntityDataToDB() catch: ${error}`);
      });
  };
}

export function submitWorkflowData(
  clientChanges,
  containerWorkflowKey,
  workflowKey,
  currentWorkflowId,
  itmId,
  stepId,
  withSignOff,
  password,
  commandName,
  commandArgument,
  certificationData,
  trainerSignOff
) {
  return (dispatch, getState) => {
    console.log(`submitWorkflowData() clientChanges ${JSON.stringify(clientChanges)}`);
    const state = getState();
    const { targetForm: formKey } = state.workflow;
    const isTokenUpdated = false;
    const parentWorkflowId = _.get(state, [
      'formDesigner',
      formKey,
      'workflowInfo',
      'parentWorkflowId',
    ]);
    const entityId = `${itmId}_${stepId}`;

    saveStorageItem('isTokenUpdated', isTokenUpdated);
    batch(() => {
      dispatch(setIsWorkflowRequestPending(true));
      // cleanup previous successes/errors/warning
      dispatch(clearSuccesses());
      dispatch(clearErrors());
      // dispatch(clearWarnings());
      dispatch({ type: START_SUBMIT_WORKFLOW_API_REQUEST });
      dispatch(setTokenStatus({ isTokenUpdated }));
    });

    return smartSolveApi
      .post('/submitworkflowdata', {
        clientChanges,
        containerWorkflowKey,
        workflowKey,
        parentWorkflowId,
        currentWorkflowId,
        itmId,
        stepId,
        tempDataStoreId: null,
        withSignOff,
        password,
        commandName,
        commandArgument,
        certificationData,
        trainerSignOff,
      })
      .then((response) => {
        const { data: responseData } = response;
        batch(() => {
          if (responseData.hasError) {
            dispatch(setIsWorkflowRequestPending(false));
            dispatch(handleErrors(responseData.errors));
            dispatch(onSubmitEnd());
            dispatch(setFinishSubmit(false));
            return;
          }

          const { SubmitWorkflowResult } = responseData;
          if (SubmitWorkflowResult && !SubmitWorkflowResult.hasError) {
            dispatch(setIsWorkflowRequestPending(false));
            dispatch(loadWorkflowStepSuccess(SubmitWorkflowResult.workFlowSteps));
            dispatch(setSubmitWorkflowResult(SubmitWorkflowResult));
            dispatch(setUserMadeInteraction(formKey, false));
            dispatch(deleteEntityFromLocalDB(entityId, false));

            // TODO: do we need this?
            if (certificationData) {
              dispatch(
                updateOriginalData(_.get(state, ['formDesigner', formKey, 'data']), formKey)
              );
            }

            const { messages = [], warnings = [] } = SubmitWorkflowResult;

            messages.forEach(({ messageNumber, message }) => {
              dispatch(addSuccess({ code: messageNumber, message }));
            });
            warnings.forEach(({ messageNumber, message }) => {
              dispatch(
                addWarning({ code: messageNumber, message, source: WARNING_SOURCES.SERVER })
              );
            });
          } else {
            dispatch(setIsWorkflowRequestPending(false));
            dispatch(loadWorkflowStepSuccess([]));
            dispatch(setSubmitWorkflowResult(null));

            const { errors = [] } = SubmitWorkflowResult ?? {};

            dispatch(handleErrors(errors));
            console.log(`submitWorkflowData() has error, data : ${JSON.stringify(responseData)}`);
          }
        });
      })
      .catch((error) => {
        dispatch(setIsWorkflowRequestPending(false));
        dispatch({ type: ABORT_SUBMIT_WORKFLOW_API_REQUEST });
        console.log(`submitWorkflowData() catch: ${JSON.stringify(error)}`);
      });
  };
}

export function submitPublicDraftData(
  clientChanges,
  containerWorkflowKey,
  workflowKey,
  currentWorkflowId,
  itmId,
  stepId
) {
  return (dispatch, getState) => {
    const state = getState();
    const { targetForm: formKey } = state.workflow;
    const isTokenUpdated = false;

    saveStorageItem('isTokenUpdated', isTokenUpdated);
    batch(() => {
      dispatch(setIsWorkflowRequestPending(true));
      dispatch(clearSuccesses());
      dispatch(clearErrors());
      dispatch({ type: START_SUBMIT_WORKFLOW_API_REQUEST });
      dispatch(setTokenStatus({ isTokenUpdated }));
    });

    return smartSolveApi
      .post('/SaveDraft', {
        clientChanges,
        containerWorkflowKey,
        workflowKey,
        currentWorkflowId,
        itmId,
        stepId,
      })
      .then((response) => {
        const { data: responseData } = response;

        batch(() => {
          if (responseData.hasError) {
            dispatch(setIsWorkflowRequestPending(false));
            dispatch(handleErrors(responseData.errors));
            dispatch(onSubmitEnd());
            dispatch(setFinishSubmit(false));
            return;
          }

          const { SubmitWorkflowResult } = responseData;
          if (SubmitWorkflowResult && !SubmitWorkflowResult.hasError) {
            dispatch(setIsWorkflowRequestPending(false));
            dispatch(loadWorkflowStepSuccess(SubmitWorkflowResult.workFlowSteps));
            dispatch(setSubmitWorkflowResult(SubmitWorkflowResult));
            dispatch(setUserMadeInteraction(formKey, false));

            const { messages = [], warnings = [] } = SubmitWorkflowResult;

            messages.forEach(({ messageNumber, message }) => {
              dispatch(addSuccess({ code: messageNumber, message }));
            });
            warnings.forEach(({ messageNumber, message }) => {
              dispatch(
                addWarning({ code: messageNumber, message, source: WARNING_SOURCES.SERVER })
              );
            });
          } else {
            dispatch(setIsWorkflowRequestPending(false));
            dispatch(loadWorkflowStepSuccess([]));
            dispatch(setSubmitWorkflowResult(null));

            const { errors = [] } = SubmitWorkflowResult ?? {};

            dispatch(handleErrors(errors));
            console.log(
              `submitPublicDraftData() has error, data : ${JSON.stringify(responseData)}`
            );
          }
        });
      })
      .catch((error) => {
        dispatch(setIsWorkflowRequestPending(false));
        dispatch({ type: ABORT_SUBMIT_WORKFLOW_API_REQUEST });
        console.log(`submitPublicDraftData() catch: ${JSON.stringify(error)}`);
      });
  };
}

export function savePublicDraft() {
  return (dispatch, getState) => {
    const {
      workflow: { targetForm },
    } = getState();

    const {
      originalData,
      data,
      workflowInfo: { containerWorkflowKey, workflowKey, workflowId, stepId, itmId },
      entityDefinition,
    } = getState().formDesigner[targetForm];

    try {
      const entityType = Object.keys(entityDefinition)[0];

      const smartSolveDiff = getSmartSolveDiff(
        originalData.entityDataStore,
        data.entityDataStore,
        entityType,
        entityDefinition
      );

      return dispatch(
        submitPublicDraftData(
          smartSolveDiff,
          containerWorkflowKey,
          workflowKey,
          workflowId,
          itmId,
          stepId
        )
      );
    } catch (error) {
      console.log(error.stack);
      return Promise.reject(error);
    }
  };
}

export function bulkUpload(formKey) {
  return async (dispatch, getState) => {
    const {
      originalData,
      data,
      workflowInfo: { containerWorkflowKey, workflowKey, stepId },
      entityDefinition,
    } = getState().formDesigner[formKey];
    try {
      const entityType = Object.keys(entityDefinition)[0];
      const clientChanges = getSmartSolveDiff(
        originalData.entityDataStore,
        data.entityDataStore,
        entityType,
        entityDefinition
      );

      console.log(`bulkUpload() clientChanges ${JSON.stringify(clientChanges)}`);
      const isTokenUpdated = false;

      saveStorageItem('isTokenUpdated', isTokenUpdated);
      batch(() => {
        dispatch(setIsWorkflowRequestPending(true));
        // cleanup previous successes/errors/warning
        dispatch(clearSuccesses());
        dispatch(clearErrors());
        // dispatch(clearWarnings());
        dispatch({ type: START_SUBMIT_WORKFLOW_API_REQUEST });
        dispatch(setTokenStatus({ isTokenUpdated }));
      });

      return sendBulkUpload({
        clientChanges,
        stepId,
        containerWorkflowKey,
        workflowKey,
      })
        .then((response) => {
          const { data: responseData } = response;
          batch(() => {
            if (responseData.hasError) {
              dispatch(setIsWorkflowRequestPending(false));
              dispatch(handleErrors(responseData.errors));
              dispatch(onSubmitEnd());
              dispatch(setFinishSubmit(false));
              return;
            }

            const { SubmitWorkflowResult } = responseData;
            if (SubmitWorkflowResult && !SubmitWorkflowResult.hasError) {
              dispatch(setIsWorkflowRequestPending(false));
              dispatch(loadWorkflowStepSuccess(SubmitWorkflowResult.workFlowSteps));
              dispatch(setSubmitWorkflowResult(SubmitWorkflowResult));
              dispatch(setUserMadeInteraction(formKey, false));

              const { messages = [], warnings = [] } = SubmitWorkflowResult;

              messages.forEach(({ messageNumber, message }) => {
                dispatch(addSuccess({ code: messageNumber, message }));
              });
              warnings.forEach(({ messageNumber, message }) => {
                dispatch(
                  addWarning({ code: messageNumber, message, source: WARNING_SOURCES.SERVER })
                );
              });
            } else {
              dispatch(setIsWorkflowRequestPending(false));
              dispatch(loadWorkflowStepSuccess([]));
              dispatch(setSubmitWorkflowResult(null));

              const { errors = [] } = SubmitWorkflowResult ?? {};

              dispatch(handleErrors(errors));
              console.log(`bulkUpload() has error, data : ${JSON.stringify(responseData)}`);
            }
          });
        })
        .catch((error) => {
          dispatch(setIsWorkflowRequestPending(false));
          dispatch({ type: ABORT_SUBMIT_WORKFLOW_API_REQUEST });
          console.log(`bulkUpload() catch: ${JSON.stringify(error)}`);
        });
    } catch (error) {
      console.log(error.stack);
      return Promise.reject(error);
    }
  };
}

export function batchUpdate() {
  return async (dispatch, getState) => {
    const {
      workflow: { targetForm },
    } = getState();
    const {
      originalData,
      data,
      entityDefinition,
      bulkEditData,
      workflowInfo: { formKey },
    } = getState().formDesigner[targetForm];

    try {
      const entityType = Object.keys(entityDefinition)[0];
      const clientChanges = getSmartSolveDiff(
        originalData.entityDataStore,
        data.entityDataStore,
        entityType,
        entityDefinition
      );

      console.log(`batchUpdate() clientChanges ${JSON.stringify(clientChanges)}`);
      const isTokenUpdated = false;

      saveStorageItem('isTokenUpdated', isTokenUpdated);
      batch(() => {
        dispatch(setIsWorkflowRequestPending(true));
        dispatch(clearSuccesses());
        dispatch(clearErrors());
        dispatch({ type: START_SUBMIT_WORKFLOW_API_REQUEST });
        dispatch(setTokenStatus({ isTokenUpdated }));
      });

      return sendBatchUpdate({
        clientChanges,
        bulkEditData,
        formKey,
      })
        .then((response) => {
          const { data: responseData } = response;

          batch(() => {
            if (responseData.hasError) {
              dispatch(setIsWorkflowRequestPending(false));
              dispatch(handleErrors(responseData.errors));
              dispatch(onSubmitEnd());
              dispatch(setFinishSubmit(false));
              return;
            }

            const { BatchUpdateResult } = responseData;
            if (BatchUpdateResult && !BatchUpdateResult.hasError) {
              dispatch(setIsWorkflowRequestPending(false));
              dispatch(loadWorkflowStepSuccess(BatchUpdateResult.workFlowSteps));
              dispatch(setSubmitWorkflowResult(BatchUpdateResult));
              dispatch(setUserMadeInteraction(targetForm, false));

              const { messages = [], warnings = [] } = BatchUpdateResult;

              messages.forEach(({ messageNumber, message }) => {
                dispatch(addSuccess({ code: messageNumber, message }));
              });
              warnings.forEach(({ messageNumber, message }) => {
                dispatch(
                  addWarning({ code: messageNumber, message, source: WARNING_SOURCES.SERVER })
                );
              });
            } else {
              dispatch(setIsWorkflowRequestPending(false));
              dispatch(loadWorkflowStepSuccess([]));
              dispatch(setSubmitWorkflowResult(null));

              const { errors = [] } = BatchUpdateResult ?? {};

              dispatch(handleErrors(errors));
              console.log(`batchUpdate() has error, data : ${JSON.stringify(responseData)}`);
            }
          });
        })
        .catch((error) => {
          dispatch(setIsWorkflowRequestPending(false));
          dispatch({ type: ABORT_SUBMIT_WORKFLOW_API_REQUEST });
          console.log(`batchUpdate() catch: ${JSON.stringify(error)}`);
        });
    } catch (error) {
      console.log(error.stack);
      return Promise.reject(error);
    }
  };
}

export function saveEntityToLocalStorage() {
  return (dispatch, getState) => {
    const {
      workflow: { commandName, commandArgument, targetForm },
    } = getState();

    const {
      originalData,
      data,
      entityDefinition,
      workflowInfo: { containerWorkflowKey, workflowKey, workflowId, stepId, itmId },
    } = getState().formDesigner[targetForm];

    try {
      const entityType = Object.keys(entityDefinition)[0];
      const smartSolveDiff = getSmartSolveDiff(
        originalData.entityDataStore,
        data.entityDataStore,
        entityType,
        entityDefinition
      );

      return dispatch(
        saveEntityDataToDB(
          smartSolveDiff,
          containerWorkflowKey,
          workflowKey,
          workflowId,
          itmId,
          stepId,
          false,
          commandName,
          commandArgument
        )
      );
    } catch (error) {
      console.log(error.stack);
      return Promise.reject(error);
    }
  };
}

export function submitEntity(password, withSignOff) {
  return (dispatch, getState) => {
    const {
      workflow: { commandName, commandArgument, targetForm },
    } = getState();

    const {
      originalData,
      data,
      entityDefinition,
      workflowInfo: {
        containerWorkflowKey,
        workflowKey,
        workflowId,
        stepId,
        itmId,
        certificationData,
        trainerSignOff,
      },
    } = getState().formDesigner[targetForm];

    try {
      const entityType = Object.keys(entityDefinition)[0];
      const smartSolveDiff = getSmartSolveDiff(
        originalData.entityDataStore,
        data.entityDataStore,
        entityType,
        entityDefinition
      );

      return dispatch(
        submitWorkflowData(
          smartSolveDiff,
          containerWorkflowKey,
          workflowKey,
          workflowId,
          itmId,
          stepId,
          withSignOff,
          password,
          commandName,
          commandArgument,
          certificationData,
          trainerSignOff
        )
      );
    } catch (error) {
      console.log(error.stack);
      return Promise.reject(error);
    }
  };
}

export const submitRequestPrint = (qsParams) => {
  return async (dispatch, getState) => {
    const { workflow, formDesigner } = getState();
    const { commandName, commandArgument, targetForm } = workflow;
    const {
      originalData,
      data,
      entityDefinition,
      workflowInfo: {
        containerWorkflowKey,
        workflowKey,
        workflowId,
        stepId,
        itmId,
        certificationData,
        parentWorkflowId,
        trainerSignOff,
      },
    } = formDesigner[targetForm];

    const entityType = Object.keys(entityDefinition)[0];
    const smartSolveDiff = getSmartSolveDiff(
      originalData.entityDataStore,
      data.entityDataStore,
      entityType,
      entityDefinition
    );
    const isBatchPrint = qsParams.isBatchPrint === 'true';
    const isTokenUpdated = false;

    if (isBatchPrint) {
      modifyBatchPrintClientChanges(smartSolveDiff, qsParams);
    }

    saveStorageItem('isTokenUpdated', isTokenUpdated);

    batch(() => {
      dispatch(setIsWorkflowRequestPending(true));
      dispatch(clearSuccesses());
      dispatch(clearErrors());
      dispatch(setTokenStatus({ isTokenUpdated }));
    });

    const printApi = isBatchPrint ? getRequestControlPrintBatch : getRequestControlPrint;

    try {
      const { data, headers } = await printApi({
        clientChanges: smartSolveDiff,
        containerWorkflowKey,
        workflowKey,
        parentWorkflowId,
        currentWorkflowId: workflowId,
        itmId: isBatchPrint ? qsParams.docId : itmId,
        stepId,
        tempDataStoreId: null,
        withSignOff: true,
        password: '',
        commandName,
        commandArgument,
        certificationData,
        trainerSignOff,
      });

      if (/json$/gi.test(headers['content-type'])) {
        // if content-type is json, it's an error response
        const reader = new FileReader();
        reader.addEventListener('loadend', () => {
          const { errors = 'Failed to request control print' } = JSON.parse(reader.result);
          batch(() => {
            dispatch(handleErrors(errors));
            dispatch(setIsWorkflowRequestPending(false));
          });
        });
        return reader.readAsText(data);
      }

      dispatch(setEnhancedModalOpen(false));

      const fileToPrint = window.URL.createObjectURL(data);

      printJS({
        printable: fileToPrint,
        onLoadingEnd: () => dispatch(setIsWorkflowRequestPending(false)),
      });

      dispatch(onSubmitEnd());
    } catch (error) {
      console.log(`getRequestControlPrint() failed: ${JSON.stringify(error)}`);
      batch(() => {
        dispatch(setIsWorkflowRequestPending(false));
        dispatch(handleErrors(error));
      });
    }
  };
};
